export const DESKTOP_HANDOFF_STORAGE_KEY = 'losi.desktop.pending-handoff.v2'
export const DESKTOP_HANDOFF_EVENT = 'losi:desktop-handoff'
export const DESKTOP_PROTOCOL_BRIDGE_READY_EVENT = 'losi:desktop-protocol-bridge-ready'

const DESKTOP_PROTOCOL_BRIDGE_READY_MARKER = '__LOSI_DESKTOP_PROTOCOL_BRIDGE_READY__'

export type DesktopHandoffAttachment = {
  id: string
  name: string
  type: string
  mimeType: string
  size: number
  bucket: string
  path: string
  kind: string
  signedUrl: string
}

export type DesktopHandoffPayload = {
  action: 'chat'
  handoffId: string
  message: string
  autoSend: boolean
  createChat: boolean
  chatId: string
  workspaceId: string
  model: string
  chatName: string
  attachments: DesktopHandoffAttachment[]
  receivedAt: number
}

function sanitizeString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function sanitizeBoolean(value: unknown, fallback = false) {
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return fallback
}

function sanitizeAttachment(input: unknown): DesktopHandoffAttachment | null {
  if (!input || typeof input !== 'object') return null

  const attachment = input as Record<string, unknown>
  const mimeType = sanitizeString(attachment.mimeType || attachment.type) || 'application/octet-stream'

  return {
    id: sanitizeString(attachment.id),
    name: sanitizeString(attachment.name) || 'Attachment',
    type: mimeType,
    mimeType,
    size: Number.isFinite(Number(attachment.size)) ? Math.max(0, Number(attachment.size)) : 0,
    bucket: sanitizeString(attachment.bucket),
    path: sanitizeString(attachment.path),
    kind: sanitizeString(attachment.kind) || (mimeType.startsWith('image/') ? 'image' : 'file'),
    signedUrl: sanitizeString(attachment.signedUrl || attachment.url)
  }
}

export function isDesktopHost() {
  if (typeof window === 'undefined') {
    return false
  }

  return Boolean(window.electronAPI) || hasDesktopUserAgent()
}

/**
 * Open an http(s) URL via Electron when available. Prefer this over
 * window.open on desktop so links leave through the shell, not a browser tab.
 * Falls back to window.open / location.assign outside the desktop host.
 */
export async function openDesktopExternal(
  href: string,
  options?: { target?: string }
): Promise<boolean> {
  const url = sanitizeString(href)
  if (!url || !/^https?:\/\//i.test(url)) {
    return false
  }

  if (typeof window === 'undefined') {
    return false
  }

  const openExternal = window.electronAPI?.openExternal
  if (typeof openExternal === 'function') {
    try {
      await Promise.resolve(openExternal(url))
      return true
    } catch {
      // Fall through to browser open if IPC fails.
    }
  }

  const target = sanitizeString(options?.target) || '_blank'
  const opened = window.open(url, target)
  if (opened) {
    try {
      opened.opener = null
    } catch {
      // Ignore cross-origin opener assignment failures.
    }
    return true
  }

  window.location.assign(url)
  return true
}

export function getDesktopPlatform() {
  if (typeof window === 'undefined') {
    return ''
  }

  return String(window.electronAPI?.platform || navigator.platform || navigator.userAgent || '')
    .trim()
    .toLowerCase()
}

export function isMacDesktopHost() {
  if (!isDesktopHost()) {
    return false
  }

  const platform = getDesktopPlatform()
  return platform.includes('darwin') || platform.includes('mac')
}

export function isWindowsDesktopHost() {
  if (!isDesktopHost()) {
    return false
  }

  const platform = getDesktopPlatform()
  return platform.includes('win32') || platform.includes('win64') || platform.includes('windows')
}

export function isLinuxDesktopHost() {
  if (!isDesktopHost()) {
    return false
  }

  const platform = getDesktopPlatform()
  return platform.includes('linux')
}

export function hasDesktopUserAgent() {
  if (typeof navigator === 'undefined') {
    return false
  }

  const userAgent = typeof navigator.userAgent === 'string' ? navigator.userAgent : ''
  // Only the real Losi Desktop shell — NOT every Electron host (Cursor, VS Code,
  // Simple Browser, etc.) or login would show "Authenticating Losi Desktop".
  return /LosiDesktop\/\d+/i.test(userAgent)
}

const DESKTOP_ELECTRON_API_WAIT_TIMEOUT_MS = 10000

export async function waitForDesktopElectronApi(options?: {
  timeoutMs?: number
  pollIntervalMs?: number
}) {
  if (typeof window === 'undefined') {
    return null
  }

  if (window.electronAPI) {
    return window.electronAPI
  }

  if (!hasDesktopUserAgent()) {
    return null
  }

  const timeoutMs =
    typeof options?.timeoutMs === 'number' && options.timeoutMs > 0
      ? Math.round(options.timeoutMs)
      : DESKTOP_ELECTRON_API_WAIT_TIMEOUT_MS
  const pollIntervalMs =
    typeof options?.pollIntervalMs === 'number' && options.pollIntervalMs > 0
      ? Math.max(25, Math.round(options.pollIntervalMs))
      : 50
  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, pollIntervalMs))

    if (window.electronAPI) {
      return window.electronAPI
    }
  }

  return window.electronAPI || null
}

export function isDesktopProtocolBridgeReady() {
  if (typeof window === 'undefined') {
    return false
  }

  return Boolean((window as any)[DESKTOP_PROTOCOL_BRIDGE_READY_MARKER])
}

function setDesktopProtocolBridgeReadyState(ready: boolean) {
  if (typeof window === 'undefined') {
    return
  }

  ;(window as any)[DESKTOP_PROTOCOL_BRIDGE_READY_MARKER] = ready
  window.dispatchEvent(
    new CustomEvent(DESKTOP_PROTOCOL_BRIDGE_READY_EVENT, {
      detail: { ready }
    })
  )
}

export function markDesktopProtocolBridgeReady() {
  setDesktopProtocolBridgeReadyState(true)
}

export function resetDesktopProtocolBridgeReady() {
  setDesktopProtocolBridgeReadyState(false)
}

export async function waitForDesktopProtocolBridgeReady(options?: {
  timeoutMs?: number
}) {
  if (typeof window === 'undefined') {
    return false
  }

  if (isDesktopProtocolBridgeReady()) {
    return true
  }

  const timeoutMs =
    typeof options?.timeoutMs === 'number' && options.timeoutMs > 0
      ? Math.round(options.timeoutMs)
      : DESKTOP_ELECTRON_API_WAIT_TIMEOUT_MS

  return await new Promise<boolean>((resolve) => {
    let settled = false
    let timeoutId = 0

    const cleanup = (result: boolean) => {
      if (settled) {
        return
      }

      settled = true
      window.removeEventListener(DESKTOP_PROTOCOL_BRIDGE_READY_EVENT, handleReady as EventListener)
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      resolve(result)
    }

    const handleReady = () => {
      if (isDesktopProtocolBridgeReady()) {
        cleanup(true)
      }
    }

    window.addEventListener(DESKTOP_PROTOCOL_BRIDGE_READY_EVENT, handleReady as EventListener)
    timeoutId = window.setTimeout(() => {
      cleanup(isDesktopProtocolBridgeReady())
    }, timeoutMs)
  })
}

const DESKTOP_PRIVATE_ROUTE_PREFIXES = [
  '/a',
  '/admin',
  '/apps',
  '/applications',
  '/assistants',
  '/blog-studio',
  '/booking',
  '/chat',
  '/dashboard',
  '/experiment',
  '/feature-flags',
  '/feedback',
  '/help',
  '/legal',
  '/notes',
  '/nexus',
  '/setup',
  '/spaces',
  '/splitview',
  '/studio',
  '/subscription',
  '/your-crew'
]

export function isDesktopPrivateRoute(pathname?: string | null) {
  if (!isDesktopHost()) {
    return false
  }

  const normalized = sanitizeString(pathname)
  if (!normalized.startsWith('/')) {
    return false
  }

  return DESKTOP_PRIVATE_ROUTE_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  )
}

export function isDesktopChatRoute(pathname?: string | null) {
  const normalized = sanitizeString(pathname)

  return normalized === '/chat' || normalized.startsWith('/chat/')
}

export function normalizeDesktopHandoffPayload(input: unknown): DesktopHandoffPayload | null {
  if (!input || typeof input !== 'object') {
    return null
  }

  const payload = input as Record<string, unknown>
  const action = sanitizeString(payload.action)
  if (action && action !== 'chat') {
    return null
  }

  return {
    action: 'chat',
    handoffId: sanitizeString(payload.handoffId),
    message: sanitizeString(payload.message),
    autoSend: sanitizeBoolean(payload.autoSend, true),
    createChat: sanitizeBoolean(payload.createChat, false),
    chatId: sanitizeString(payload.chatId || payload.chat_id),
    workspaceId: sanitizeString(payload.workspaceId || payload.workspace_id),
    model: sanitizeString(payload.model),
    chatName: sanitizeString(payload.chatName || payload.name),
    attachments: Array.isArray(payload.attachments)
      ? payload.attachments
          .map(sanitizeAttachment)
          .filter((attachment): attachment is DesktopHandoffAttachment => attachment !== null)
      : [],
    receivedAt: Number.isFinite(Number(payload.receivedAt)) ? Number(payload.receivedAt) : Date.now()
  }
}

function readDesktopHandoffQueue(): DesktopHandoffPayload[] {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const raw = window.sessionStorage.getItem(DESKTOP_HANDOFF_STORAGE_KEY)
    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed
      .map(normalizeDesktopHandoffPayload)
      .filter((payload): payload is DesktopHandoffPayload => payload !== null)
  } catch {
    return []
  }
}

function writeDesktopHandoffQueue(queue: DesktopHandoffPayload[]) {
  if (typeof window === 'undefined') {
    return
  }

  if (queue.length === 0) {
    window.sessionStorage.removeItem(DESKTOP_HANDOFF_STORAGE_KEY)
    return
  }

  window.sessionStorage.setItem(DESKTOP_HANDOFF_STORAGE_KEY, JSON.stringify(queue))
}

export function readPendingDesktopHandoff(): DesktopHandoffPayload | null {
  return readDesktopHandoffQueue()[0] || null
}

export function writePendingDesktopHandoff(payload: DesktopHandoffPayload) {
  const normalizedPayload = normalizeDesktopHandoffPayload(payload)
  if (!normalizedPayload || typeof window === 'undefined') {
    return
  }

  const queue = normalizedPayload.handoffId
    ? readDesktopHandoffQueue().filter((item) => item.handoffId !== normalizedPayload.handoffId)
    : readDesktopHandoffQueue()

  queue.push({
    ...normalizedPayload,
    receivedAt: Date.now()
  })

  writeDesktopHandoffQueue(queue)
  dispatchDesktopHandoffEvent()
}

export function clearPendingDesktopHandoff() {
  writeDesktopHandoffQueue([])
}

export function consumePendingDesktopHandoff(handoffId = '') {
  if (typeof window === 'undefined') {
    return
  }

  const queue = readDesktopHandoffQueue()
  if (queue.length === 0) {
    return
  }

  const nextQueue =
    handoffId.trim().length > 0
      ? queue.filter((payload, index) => !(index === 0 && payload.handoffId === handoffId))
      : queue.slice(1)

  writeDesktopHandoffQueue(nextQueue)
  if (nextQueue.length > 0) {
    dispatchDesktopHandoffEvent()
  }
}

export function dispatchDesktopHandoffEvent() {
  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(new CustomEvent(DESKTOP_HANDOFF_EVENT))
}

export function onPendingDesktopHandoff(callback: () => void) {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const listener = () => callback()
  window.addEventListener(DESKTOP_HANDOFF_EVENT, listener)
  return () => window.removeEventListener(DESKTOP_HANDOFF_EVENT, listener)
}

export function getDesktopChatRoute(payload: DesktopHandoffPayload) {
  if (payload.createChat || !payload.chatId) {
    return '/chat'
  }

  return `/chat/${payload.chatId}`
}

export function normalizeDesktopRouteTarget(
  value: string | null | undefined,
  fallback = '/chat'
) {
  return normalizeDesktopRouteTargetInternal(value, fallback, 0)
}

function normalizeDesktopRouteTargetInternal(
  value: string | null | undefined,
  fallback = '/chat',
  depth = 0
) {
  const normalizedFallback =
    typeof fallback === 'string' && fallback.startsWith('/') && !fallback.startsWith('//')
      ? fallback
      : '/chat'
  const raw = sanitizeString(value)

  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return normalizedFallback
  }

  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost'
    const url = new URL(raw, baseOrigin)
    if (url.origin !== baseOrigin || url.pathname === '/desktop') {
      return normalizedFallback
    }

    if (url.pathname === '/login') {
      const nestedNext = url.searchParams.get('next')
      if (nestedNext && depth < 5) {
        return normalizeDesktopRouteTargetInternal(nestedNext, normalizedFallback, depth + 1)
      }

      return normalizedFallback
    }

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return normalizedFallback
  }
}

export function getDesktopAuthFlag(currentValue?: string | null) {
  const normalized = sanitizeString(currentValue)
  if (normalized) {
    return normalized
  }

  return isDesktopHost() ? '1' : ''
}

export function getDesktopLoginRoute(currentPath?: string | null) {
  const fallbackPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '/chat'
  const nextPath = normalizeDesktopRouteTarget(currentPath || fallbackPath)
  const loginParams = new URLSearchParams()
  loginParams.set('desktop', '1')
  loginParams.set('next', nextPath)
  return `/login?${loginParams.toString()}`
}

export async function reportPendingDesktopHandoffResult(
  handoffId: string,
  ok: boolean,
  error = ''
) {
  if (!handoffId || typeof window === 'undefined') {
    return
  }

  const electronAPI = await waitForDesktopElectronApi()
  if (!electronAPI?.reportLosiHandoffResult) {
    return
  }

  await Promise.resolve(
    electronAPI.reportLosiHandoffResult({
      handoffId,
      ok,
      error: error || null
    })
  )
}
